import React, { useState } from "react";
import { Check, ChevronRight, ChevronLeft, CalendarCheck } from "lucide-react";
import { QUIZ_STEPS, ACUITY_URL, ACUITY_NOTES_FIELD_ID, OFFER, DISQUALIFY, DISQUALIFIED_MESSAGE, CRM_LEAD_URL, CRM_SOURCE, CRM_SECRET, LEGAL_NAME, HIDE_PHONE_FIELD, CONTACT_FIRST } from "../mock";
import { useToast } from "../hooks/use-toast";
import { trackPixel } from "../lib/pixel";

// Multi-step qualifying quiz that ends in contact capture -> Acuity booking
export const QuizForm = ({ onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [smsConsent, setSmsConsent] = useState(false);
  const slug = window.location.pathname.replace(/^\/+|\/+$/g, "").split("/")[0];
  // Immediately disqualify when a disqualifying option is chosen
  const [disqualifiedNow, setDisqualifiedNow] = useState(false);

  // Order the screens: contact-first pages ask for details before the questions.
  const stepList = CONTACT_FIRST
    ? [{ type: "contact" }, ...QUIZ_STEPS.map((s) => ({ type: "question", data: s }))]
    : [...QUIZ_STEPS.map((s) => ({ type: "question", data: s })), { type: "contact" }];
  const totalSteps = stepList.length;
  const current = stepList[step] || stepList[0];
  const isContactStep = current.type === "contact";
  const isLastStep = step === totalSteps - 1;
  const questionData = current.type === "question" ? current.data : null;
  const questionNumber = questionData ? QUIZ_STEPS.findIndex((s) => s.key === questionData.key) + 1 : 0;

  const isDisqualified = (a) => a.budget === DISQUALIFY.budget || a.timing === DISQUALIFY.timing;

  const finish = (allAnswers, disq) => {
    // Persist so downstream steps can match the client
    try {
      localStorage.setItem("db_active_lead", JSON.stringify({ ...contact, answers: allAnswers, slug: window.location.pathname }));
    } catch (_) {}
    if (onComplete) onComplete({ ...contact, answers: allAnswers, disqualified: disq });
  };

  // Persist + push the lead to CRM / GHL, then either disqualify or hand off to scheduling
  const completeAll = (allAnswers) => {
    const disq = isDisqualified(allAnswers);
    const lead = { ...allAnswers, ...contact, at: new Date().toISOString() };
    const prev = JSON.parse(localStorage.getItem("descent_leads") || "[]");
    localStorage.setItem("descent_leads", JSON.stringify([lead, ...prev]));
    // Send EVERY finisher (qualified or not) to the CRM pipeline if configured
    if (CRM_LEAD_URL && !CRM_LEAD_URL.includes("ACCOUNT_ID")) {
      fetch(CRM_LEAD_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(CRM_SECRET ? { "X-Capture-Secret": CRM_SECRET } : {}),
        },
        body: JSON.stringify({
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          source: CRM_SOURCE,
          status: disq ? "not_ready" : "qualified",
          answers: {
            "Area that bothers you": allAnswers.area,
            "Wants results": allAnswers.results,
            "Ready to invest": allAnswers.budget,
            "When to come in": allAnswers.timing,
          },
        }),
      }).catch((err) => console.warn("CRM lead post failed", err));
    }
    // Forward form data to the location's GoHighLevel webhook (server-side, avoids CORS)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead/forward`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slug,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        source: CRM_SOURCE,
        status: disq ? "not_ready" : "qualified",
        answers: {
          area_of_concern: allAnswers.area,
          wants_results: allAnswers.results,
          ready_to_invest: allAnswers.budget,
          preferred_timing: allAnswers.timing,
        },
      }),
    }).catch((err) => console.warn("GHL forward failed", err));
    trackPixel("Lead", { content_name: OFFER.name, value: OFFER.price, currency: "USD" });
    if (disq) setDisqualifiedNow(true);
    else finish(allAnswers, disq);
  };

  const selectOption = (key, value) => {
    const merged = { ...answers, [key]: value };
    setAnswers(merged);
    setTimeout(() => {
      if (step + 1 >= totalSteps) completeAll(merged); // answered the final question
      else setStep((s) => s + 1);
    }, 220);
  };

  const submitContact = (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || (!HIDE_PHONE_FIELD && !contact.phone)) {
      toast({ title: "Please complete all fields", description: HIDE_PHONE_FIELD ? "We need your name and email to hold your spot." : "We need your name, phone and email to hold your spot." });
      return;
    }
    if (!HIDE_PHONE_FIELD && !smsConsent) {
      toast({ title: "Please agree to receive text messages", description: "We need your consent to send appointment reminders and confirmations." });
      return;
    }
    if (isLastStep) completeAll(answers); // contact-last flow
    else setStep((s) => s + 1); // contact-first: continue to the questions
  };

  const progress = Math.round(((step + (disqualifiedNow ? 1 : 0)) / totalSteps) * 100);

  return (
    <div id="quiz-form" className="overflow-hidden rounded-3xl bg-white p-6 shadow-[0_30px_80px_-30px_rgba(35,35,32,0.35)] ring-1 ring-brand-cream md:p-8">
      {/* progress */}
      <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-brand-cream">
        <div className="h-full rounded-full bg-brand-gold transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      {disqualifiedNow ? (
        <div className="animate-fade-in py-6 text-center">
          <h3 className="font-serif text-2xl leading-snug text-brand-ink">
            {DISQUALIFIED_MESSAGE.headline}
          </h3>
          <p className="mx-auto mt-4 max-w-sm font-sans text-[15px] text-brand-muted">
            {DISQUALIFIED_MESSAGE.body}
          </p>
          <button
            onClick={() => finish(answers, true)}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-sage px-8 py-3.5 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-brand-ivory transition hover:bg-brand-sagedark"
          >
            {DISQUALIFIED_MESSAGE.cta}
          </button>
          <button onClick={() => setDisqualifiedNow(false)} className="mx-auto mt-5 flex items-center gap-1 font-sans text-sm text-brand-muted transition hover:text-brand-ink">
            <ChevronLeft size={16} /> Go back
          </button>
        </div>
      ) : isContactStep ? (
        <form onSubmit={submitContact} className="animate-fade-in">
          <h3 className="font-serif text-xl text-brand-ink md:text-2xl">{CONTACT_FIRST ? "First — let's get your details" : "Last step — where do we send your confirmation?"}</h3>
          <p className="mt-1 font-sans text-sm text-brand-muted">No spam. Just your appointment details.</p>
          <div className="mt-5 space-y-3">
            <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Full Name" className="w-full rounded-xl border border-brand-cream bg-brand-ivory px-4 py-3 font-sans text-sm text-brand-ink outline-none transition focus:border-brand-sage focus:ring-2 focus:ring-brand-sage/20" />
            {!HIDE_PHONE_FIELD && (
              <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Phone *" className="w-full rounded-xl border border-brand-cream bg-brand-ivory px-4 py-3 font-sans text-sm text-brand-ink outline-none transition focus:border-brand-sage focus:ring-2 focus:ring-brand-sage/20" />
            )}
            <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email *" type="email" className="w-full rounded-xl border border-brand-cream bg-brand-ivory px-4 py-3 font-sans text-sm text-brand-ink outline-none transition focus:border-brand-sage focus:ring-2 focus:ring-brand-sage/20" />
          </div>
          {!HIDE_PHONE_FIELD && (
          <label className="mt-4 flex items-start gap-2.5 text-left">
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              data-testid="sms-consent-checkbox"
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-brand-cream text-brand-sage accent-brand-sage focus:ring-brand-sage/30"
            />
            <span className="font-sans text-[11px] leading-relaxed text-brand-muted">
              I agree to receive appointment reminder &amp; confirmation text messages from {LEGAL_NAME} at the number provided. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. See our{" "}
              <a href={`/privacy?slug=${slug}`} target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-ink">Privacy Policy</a> &amp;{" "}
              <a href={`/terms?slug=${slug}`} target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-ink">Terms</a>.
            </span>
          </label>
          )}
          <div className="mt-6 flex items-center gap-3">
            {step > 0 && (
              <button type="button" onClick={() => setStep((s) => s - 1)} className="flex items-center gap-1 rounded-full border border-brand-cream px-4 py-3 font-sans text-sm text-brand-muted transition hover:bg-brand-ivory">
                <ChevronLeft size={16} /> Back
              </button>
            )}
            <button type="submit" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-brand-sage px-6 py-3.5 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-brand-ivory transition hover:bg-brand-sagedark">
              {isLastStep ? `Reserve My $${OFFER.price} Spot` : "Continue"}
            </button>
          </div>
        </form>
      ) : (
        <div key={step} className="animate-fade-in">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-brand-golddark">
            Question {questionNumber} of {QUIZ_STEPS.length}
          </p>
          <h3 className="mt-2 font-serif text-xl leading-snug text-brand-ink md:text-2xl">
            {questionData.question}
          </h3>
          {questionData.image && (
            <div className="mt-4 overflow-hidden rounded-xl border border-brand-cream">
              <img
                src={questionData.image}
                alt="Example results"
                className="h-44 w-full object-cover"
              />
            </div>
          )}
          <div className="mt-5 space-y-2.5">
            {questionData.options.map((opt) => {
              const active = answers[questionData.key] === opt;
              return (
                <button
                  key={opt}
                  onClick={() => selectOption(questionData.key, opt)}
                  className={`flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-left font-sans text-sm transition-all duration-200 ${
                    active
                      ? "border-brand-sage bg-brand-sage/5 text-brand-ink"
                      : "border-brand-cream bg-white text-brand-ink hover:border-brand-sage hover:bg-brand-ivory"
                  }`}
                >
                  <span>{opt}</span>
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${active ? "border-brand-sage bg-brand-sage text-white" : "border-brand-cream text-transparent"}`}>
                    <Check size={14} />
                  </span>
                </button>
              );
            })}
          </div>
          {step > 0 && (
            <button onClick={() => setStep((s) => s - 1)} className="mt-5 flex items-center gap-1 font-sans text-sm text-brand-muted transition hover:text-brand-ink">
              <ChevronLeft size={16} /> Back
            </button>
          )}
          {step === 0 && (
            <p className="mt-5 flex items-center gap-1 font-sans text-xs text-brand-muted">
              Takes 20 seconds <ChevronRight size={13} />
            </p>
          )}
        </div>
      )}
    </div>
  );
};
